import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { Heart, Minus, Plus, Truck, RotateCcw, MessageCircle } from "lucide-react";
import { toast } from "sonner";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { api, productWaMessage, waLink } from "@/lib/api";
import { useStore } from "@/context/StoreContext";
import { Gallery } from "@/components/product/Gallery";
import { PriceTag, ProductCard } from "@/components/ProductCard";
import { UniqueBadge } from "@/components/Logo";
import { t } from "@/lib/i18n";
import { BUSINESS } from "@/lib/brand";

const Availability = ({ stock }) => {
  if (stock === 0) return <span data-testid="product-availability" className="inline-flex items-center gap-2 text-sm font-semibold text-[#8a2f22]"><span className="w-2 h-2 rounded-full bg-[#8a2f22]" />{t("soldOut")}</span>;
  if (stock === 1) return <span data-testid="product-availability" className="inline-flex items-center gap-2 text-sm text-[#3d5a3a]"><span className="w-2 h-2 rounded-full bg-[#3d5a3a]" />{t("available")} · esemplare singolo</span>;
  if (stock <= 3) return <span data-testid="product-availability" className="inline-flex items-center gap-2 text-sm text-[#956842]"><span className="w-2 h-2 rounded-full bg-[#956842]" />{t("lowStock")} ({stock})</span>;
  return <span data-testid="product-availability" className="inline-flex items-center gap-2 text-sm text-[#3d5a3a]"><span className="w-2 h-2 rounded-full bg-[#3d5a3a]" />{t("available")}</span>;
};

const Spec = ({ label, value, testId }) => (
  <div className="flex justify-between py-2 border-b border-[#D5B98C]/30 text-sm">
    <span className="text-[#242321]/60">{label}</span>
    <span data-testid={testId} className="text-right">{value}</span>
  </div>
);

export default function ProductDetail() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { addToCart, isFavorite, toggleFavorite, setCartOpen } = useStore();
  const [p, setP] = useState(null);
  const [related, setRelated] = useState([]);
  const [qty, setQty] = useState(1);
  const [variant, setVariant] = useState(null);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    setP(null); setQty(1); setVariant(null); setNotFound(false);
    api.get(`/products/${slug}`).then((r) => { setP(r.data); setVariant(r.data.variants[0]?.label ?? null); }).catch(() => setNotFound(true));
    api.get(`/products/${slug}/related`).then((r) => setRelated(r.data)).catch(() => {});
  }, [slug]);

  if (notFound) return <div className="py-32 text-center font-serif text-3xl" data-testid="product-not-found">Prodotto non trovato.</div>;
  if (!p) return <div className="max-w-[1440px] mx-auto px-5 sm:px-8 pt-10"><div className="aspect-[4/5] lg:w-1/2 bg-[#E9DFCE] animate-pulse" /></div>;

  const soldOut = p.stock === 0;
  const add = () => { addToCart(p, qty, variant); toast.success(`${p.name} aggiunto al carrello`); };
  const buyNow = () => { addToCart(p, qty, variant); navigate("/checkout"); };

  return (
    <div className="max-w-[1440px] mx-auto px-5 sm:px-8 pt-8" data-testid="product-detail-page">
      <nav className="text-xs text-[#242321]/60 mb-6 flex gap-2" data-testid="product-breadcrumb">
        <Link to="/collezione" className="hover:text-[#956842]">Collezione</Link><span>/</span>
        <Link to={`/categoria/${p.category}`} className="hover:text-[#956842]">{p.category_name}</Link>
      </nav>
      <div className="grid lg:grid-cols-[1.15fr_1fr] gap-10 lg:gap-16">
        <Gallery images={p.images} videos={(p.videos || []).map((v) => v.url)} name={p.name} demo={p.demo} alts={p.image_alts} />
        <div className="lg:sticky lg:top-32 self-start">
          <div className="text-[11px] uppercase tracking-[0.25em] text-[#956842]">{p.category_name}</div>
          <h1 data-testid="product-title" className="font-serif text-4xl sm:text-5xl text-[#39251D] leading-[1.02] mt-2">{p.name}</h1>
          <div className="text-xs text-[#242321]/50 mt-2 tracking-wider" data-testid="product-sku">SKU {p.sku}</div>
          <PriceTag p={p} className="mt-5 text-2xl" />
          <p className="mt-5 text-[15px] leading-relaxed text-[#242321]/80" data-testid="product-short-description">{p.short_description}</p>
          <div className="mt-5 flex flex-wrap items-center gap-3"><Availability stock={p.stock} />{p.stock === 1 && <UniqueBadge testId="product-unique-badge" />}</div>

          {p.color && (
          <div className="mt-6 flex items-center gap-3 text-sm">
            <span className="w-5 h-5 rounded-full border border-[#D5B98C]" style={{ background: p.color_hex }} />
            <span data-testid="product-color">Colore: {p.color}</span>
          </div>
          )}

          {p.variants.length > 0 && (
            <div className="mt-6">
              <div className="text-[11px] uppercase tracking-[0.22em] text-[#956842] mb-3">Variante</div>
              <div className="flex flex-wrap gap-2">
                {p.variants.map((v) => (
                  <button key={v.label} data-testid={`product-variant-${v.label.replace(/\s+/g, "-")}`} onClick={() => setVariant(v.label)} className={`px-4 py-2 text-sm border transition-colors ${variant === v.label ? "border-[#39251D] bg-[#39251D] text-[#F5F0E8]" : "border-[#D5B98C] hover:border-[#39251D]"}`}>
                    {v.label}
                  </button>
                ))}
              </div>
            </div>
          )}

          <div className="mt-8 flex gap-3">
            <div className={`inline-flex items-center border border-[#D5B98C] ${soldOut ? "opacity-40 pointer-events-none" : ""}`}>
              <button data-testid="product-qty-decrease" onClick={() => setQty((q) => Math.max(1, q - 1))} className="p-3.5"><Minus className="w-3.5 h-3.5" /></button>
              <span data-testid="product-qty" className="w-8 text-center">{qty}</span>
              <button data-testid="product-qty-increase" onClick={() => setQty((q) => Math.min(p.stock, q + 1))} className="p-3.5"><Plus className="w-3.5 h-3.5" /></button>
            </div>
            <button data-testid="product-detail-add-to-cart" disabled={soldOut} onClick={add} className="flex-1 bg-[#39251D] text-[#F5F0E8] text-xs uppercase tracking-[0.2em] hover:bg-[#956842] transition-colors disabled:bg-[#242321]/30 disabled:cursor-not-allowed">
              {soldOut ? t("soldOut") : t("addToCart")}
            </button>
            <button data-testid="product-detail-favorite" onClick={() => toggleFavorite(p)} aria-label={t("favorites")} className="w-[52px] border border-[#D5B98C] flex items-center justify-center hover:border-[#39251D] transition-colors">
              <Heart className={`w-5 h-5 ${isFavorite(p.slug) ? "fill-[#956842] text-[#956842]" : ""}`} strokeWidth={1.5} />
            </button>
          </div>
          <button data-testid="product-detail-buy-now" disabled={soldOut} onClick={buyNow} className="mt-3 w-full py-4 border border-[#39251D] text-[#39251D] text-xs uppercase tracking-[0.2em] hover:bg-[#39251D] hover:text-[#F5F0E8] transition-colors disabled:opacity-40 disabled:cursor-not-allowed">
            {t("buyNow")}
          </button>
          <a data-testid="product-whatsapp-link" href={waLink(productWaMessage(p))} target="_blank" rel="noopener noreferrer" className="mt-4 inline-flex items-center gap-2 text-sm text-[#956842] hover:text-[#39251D]">
            <MessageCircle className="w-4 h-4" /> Chiedi informazioni su WhatsApp
          </a>

          <div className="mt-8 grid grid-cols-2 gap-3 text-xs">
            <Link to="/spedizioni" className="flex gap-2 p-3 bg-[#E9DFCE]/60 hover:bg-[#E9DFCE] transition-colors"><Truck className="w-4 h-4 text-[#956842] shrink-0" />Italia 6,90 € · gratuita da 99 €</Link>
            <Link to="/resi" className="flex gap-2 p-3 bg-[#E9DFCE]/60 hover:bg-[#E9DFCE] transition-colors"><RotateCcw className="w-4 h-4 text-[#956842] shrink-0" />Reso gratuito entro 14 giorni</Link>
          </div>

          <Accordion type="multiple" defaultValue={["dettagli"]} className="mt-8" data-testid="product-accordion">
            <AccordionItem value="dettagli" className="border-[#D5B98C]/50">
              <AccordionTrigger data-testid="accordion-dettagli" className="font-serif text-lg">Descrizione</AccordionTrigger>
              <AccordionContent className="text-[15px] leading-relaxed text-[#242321]/80" data-testid="product-description">{p.description}</AccordionContent>
            </AccordionItem>
            <AccordionItem value="materiali" className="border-[#D5B98C]/50">
              <AccordionTrigger data-testid="accordion-materiali" className="font-serif text-lg">Materiali e dimensioni</AccordionTrigger>
              <AccordionContent>
                {p.brand && <Spec label="Marca" value={p.brand} testId="product-brand" />}
                {p.model && <Spec label="Modello" value={p.model} testId="product-model" />}
                <Spec label="Materiale" value={p.material} testId="product-material" />
                <Spec label="Dimensioni" value={p.dimensions} testId="product-dimensions" />
                <Spec label="Colore" value={[p.color, ...(p.secondary_colors || [])].filter(Boolean).join(", ")} testId="product-color-spec" />
              </AccordionContent>
            </AccordionItem>
            <AccordionItem value="spedizioni" className="border-[#D5B98C]/50">
              <AccordionTrigger data-testid="accordion-spedizioni" className="font-serif text-lg">Spedizioni, resi e assistenza</AccordionTrigger>
              <AccordionContent className="text-sm leading-relaxed text-[#242321]/80 space-y-2">
                <p>Italia: 6,90 €, gratuita da 99 € · UE: 12,90 €, gratuita da 149 €. Preparazione in 1–2 giorni lavorativi, corriere BRT.</p>
                <p>Consulta <Link to="/resi" className="underline">Resi e diritto di recesso</Link> e <Link to="/spedizioni" className="underline">Spedizioni</Link>.</p>
                <p>Per assistenza: WhatsApp o telefono <a href={BUSINESS.phoneHref} className="underline">{BUSINESS.phone}</a>, email <a href={`mailto:${BUSINESS.email}`} className="underline">{BUSINESS.email}</a>.</p>
              </AccordionContent>
            </AccordionItem>
          </Accordion>
        </div>
      </div>

      {related.length > 0 && (
        <section className="pt-24" data-testid="related-products">
          <h2 className="font-serif text-3xl sm:text-4xl text-[#39251D] mb-10">Potrebbe piacerti anche</h2>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-x-4 gap-y-10 sm:gap-x-6">
            {related.map((r) => <ProductCard key={r.slug} p={r} />)}
          </div>
        </section>
      )}
    </div>
  );
}
